import ApiClient from '../backend/ApiClient'
import { DataViewMode, FileDataSource } from '../components/routes/ContractDetails'
import { IPaginationQueryResponse } from '../definitions/Common'
import { ICostUtilization } from '../definitions/DataAnalytics'
import { IProviderExpense, ITosExpense } from '../definitions/ServiceExpense'
import CSVManager from './CSVManager'

interface ICostUtilizationFormatConfig {
    format: string
    decimalDigits: number
    name: string
    hidden?: boolean
}

interface ICostUtilizationWithFormat {
    value?: ICostUtilization[keyof ICostUtilization]
    format?: string
    decimalDigits?: number
}

type TosExpenseWithFormat = ITosExpense & {
    format?: string
    decimalDigits?: number
}

class ContractsAnalyticsManagerClass {
    costUtilizationConfig: Record<Exclude<keyof ICostUtilization, 'uuid'>, ICostUtilizationFormatConfig> = {
        horizon: {
            format: 'text',
            decimalDigits: 0,
            name: 'Horizon'
        },
        practiceGroup: {
            format: 'text',
            decimalDigits: 0,
            name: 'Practice Unit'
        },
        ageGroup: {
            format: 'text',
            decimalDigits: 0,
            name: 'Age Group'
        },
        tosLevelOne: {
            format: 'text',
            decimalDigits: 0,
            name: 'TOS Level One'
        },
        tosLevelTwo: {
            format: 'text',
            decimalDigits: 0,
            name: 'TOS Level Two'
        },
        tosLevelThree: {
            format: 'text',
            decimalDigits: 0,
            name: 'TOS Level Three'
        },
        entityTCOC: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Entity TCOC'
        },
        entityMemberMonths: {
            format: 'decimal',
            decimalDigits: 2,
            name: 'Member Months'
        },
        networkMemberMonths: {
            // Note: Needs to be not used
            format: 'decimal',
            decimalDigits: 0,
            name: 'Network Member Months',
            hidden: true
        },
        entityPMPMUnadjusted: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Entity PMPM (Unadjusted)'
        },
        networkPMPMUnadjusted: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Network PMPM (Unadjusted)'
        },
        networkPMPMRisk: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Network PMPM (Risk Adj. to Entity)'
        },
        diffNetworkEventsRisk: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Difference to Network PMPM (Risk Adj. to Entity)'
        },
        entityEvents: {
            format: 'decimal',
            decimalDigits: 2,
            name: 'Entity Events'
        },
        entityEventsDiv1000Unadjusted: {
            format: 'decimal',
            decimalDigits: 2,
            name: 'Entity Events / 1000 (Unadjusted)'
        },
        networkEventsDiv1000Unadjusted: {
            format: 'decimal',
            decimalDigits: 2,
            name: 'Network Events / 1000 (Unadjusted)'
        },
        networkEventsDiv1000Adjusted: {
            format: 'decimal',
            decimalDigits: 2,
            name: 'Network Events / 1000 (Risk Adj. to Entity)'
        },
        entityTCOCDivEvents: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Entity TCOC / Entity Events'
        },
        networkTCOCDivEntityEvents: {
            format: 'currency',
            decimalDigits: 2,
            name: 'Network TCOC / Entity Events'
        },
        diffNetworkTCOCDivEntityEvents: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Difference to Network TCOC / Entity Events'
        },
        diffNetworkEventsDiv1000Adjusted: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Difference to Network Events / 1000 (Risk Adj. to Entity)'
        },
        trendEntityPMPM: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Entity Trend (PMPM)'
        },
        trendUnadjustedNetworkPMPM: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Unadjusted Network Trend (PMPM)'
        },
        trendEntityEventsDiv1000: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Entity Trend (Events / 1000)'
        },
        trendUnadjustedNetworkEventsDiv1000: {
            format: 'percent',
            decimalDigits: 2,
            name: 'Unadjusted Network Trend (Events / 1000)'
        }
    }

    prepareFileDownload = async (contractId: number, startDate: string, endDate: string, source?: FileDataSource, dataViewMode?: DataViewMode) => {
        let result: true | null = true
        try {
            if (!source || source === 'expenseByTos') {
                let totalExpense: ITosExpense[]
                if (dataViewMode === 'pmpm') {
                    totalExpense = await ApiClient.getContractTotalExpensePMPMByPeriod({ contractId, startDate, endDate })
                } else {
                    totalExpense = await ApiClient.getContractTotalExpenseByPeriod({ contractId, startDate, endDate })
                }

                if (!totalExpense.some(item => item.value > 0)) result = null

                const dataWithFormat = this.addFormatToExpensesValue(totalExpense)
                const formattedData = this.formatExpensesData(dataWithFormat)

                CSVManager.downloadCSV(
                    [formattedData.map(el => el.value)],
                    totalExpense.map(header => header.name),
                    `Expenses by TOS ${dataViewMode === 'pmpm' ? 'PMPM' : ''} - From ${startDate} To ${endDate}`
                )
            }
            if (!source || source === 'providerExpenseByTos') {
                let providerExpense: IPaginationQueryResponse<IProviderExpense>
                if (dataViewMode === 'pmpm') {
                    providerExpense = await ApiClient.getContractExpensesPMPMGroupedByProvider({ contractId, startDate, endDate })
                } else {
                    providerExpense = await ApiClient.getContractExpensesGroupedByProvider({ contractId, startDate, endDate })
                }

                if (!providerExpense || !providerExpense.items || providerExpense.items.length === 0) result = null

                const dataWithFormat = this.addFormatToProviderExpensesValue(providerExpense.items)
                const formattedData = this.formatProviderExpensesData(dataWithFormat).map(el => [el.providerName, ...el.expenses.map(exp => exp.value)])

                CSVManager.downloadCSV(
                    [...formattedData],
                    ['Provider', ...providerExpense.items[0].expenses.map(exp => exp.name)],
                    `Providers expenses by TOS ${dataViewMode === 'pmpm' ? 'PMPM' : ''} - From ${startDate} To ${endDate}`
                )
            }
            if (!source || source === 'costUtilizationPreview') {
                const { fileUrl, fileKey } = await ApiClient.getContractCostUtilizationReport({ contractId, startDate, endDate, limit: 5 })
                CSVManager.downloadCSVWithoutConversion(fileUrl, fileKey)
            }
            if (!source || source === 'costUtilization') {
                const { fileUrl, fileKey } = await ApiClient.getContractCostUtilizationReport({ contractId, startDate, endDate })
                CSVManager.downloadCSVWithoutConversion(fileUrl, fileKey)
            }
        } catch (error) {
            console.error('Error during the file creation:', error)
            result = null
        } finally {
            return result
        }
    }

    // For CSV data elements use indexes and for CSV headers use the exact string to remove the whole column
    stripFieldFromData = (data: string[] | string [][], fields: string[]) => {
        if (!data || data.length <= 0 || fields.length <= 0) return []
        if (Array.isArray(data[0])) {
            return (data as string[][]).map((innerArray: string[]) => {
                const parsedFields = fields.map(el => Number(el))
                return innerArray.filter((_, index) => !parsedFields.includes(index))
            })
        } else {
            return (data as string[]).filter(item => !fields.includes(String(item)))
        }
    }

    addFormatToProviderExpensesValue = (data: IProviderExpense[]) => {
        if (data?.length <= 0) return []

        const dataWithFormat = JSON.parse(JSON.stringify(data)) // Provider Expenses => { providerName, expenses[] }[]
        const dataLength = data.length

        for (let i = 0; i < dataLength; i++) {
            const obj: TosExpenseWithFormat[] = dataWithFormat[i].expenses

            const elementLength = obj.length
            for (let idx = 0; idx < elementLength; idx++) {
                const expenseName = obj[idx].name
                let format: 'decimal' | 'currency' | 'percent' = 'decimal'
                let decimalDigits = 2

                switch (expenseName) {
                    // TOS expenses
                    case 'Additional':
                    case 'Professional':
                    case 'Inpatient':
                    case 'Outpatient':
                    case 'Pharmacy':
                    case 'Other':
                        format = 'currency'
                        decimalDigits = 2
                        break
                }

                obj[idx] = {
                    ...obj[idx],
                    format: format,
                    decimalDigits: decimalDigits
                }
            }
        }
        return dataWithFormat
    }

    addFormatToExpensesValue = (data: ITosExpense[]) => {
        if (data?.length <= 0) return []

        const dataWithFormat = JSON.parse(JSON.stringify(data))
        const dataLength = data.length

        for (let i = 0; i < dataLength; i++) {
            const obj: TosExpenseWithFormat = dataWithFormat[i]

            const expenseName = obj.name
            let format: 'decimal' | 'currency' | 'percent' = 'decimal'
            let decimalDigits = 2

            switch (expenseName) {
                // TOS expenses
                case 'Additional':
                case 'Professional':
                case 'Inpatient':
                case 'Outpatient':
                case 'Pharmacy':
                case 'Other':
                    format = 'currency'
                    decimalDigits = 2
                    break
            }

            dataWithFormat[i] = {
                ...dataWithFormat[i],
                format: format,
                decimalDigits: decimalDigits
            }
        }
        return dataWithFormat
    }

    addFormatToCostUtilizationValue = (data: ICostUtilization[][], headers: string[]): ICostUtilizationWithFormat[][] => {
        if (data?.length <= 0 || headers?.length <= 0) return []

        const formattedData = JSON.parse(JSON.stringify(data))
        for (let i = 0; i < formattedData.length; i++) {
            const element = formattedData[i]
            for (let idx = 0; idx < element.length; idx++) {
                let format = 'decimal'
                let decimalDigits = 0
                const config = this.costUtilizationConfig[headers[idx]]
                if (config?.format) format = config.format
                if (config?.decimalDigits) decimalDigits = config.decimalDigits

                element[idx] = {
                    value: element[idx],
                    format,
                    decimalDigits
                }
            }
        }
        return formattedData
    }

    addFormatToCostUtilizationHeader = (headers: string[]) => {
        if (headers?.length <= 0) return []

        const formattedHeaders = JSON.parse(JSON.stringify(headers))
        for (let i = 0; i < formattedHeaders.length; i++) {
            let name = 'N/A'
            const config = this.costUtilizationConfig[formattedHeaders[i]]
            if (config?.name) name = config.name
            formattedHeaders[i] = name
        }
        return formattedHeaders
    }

    formatProviderExpensesData = (data: IProviderExpense[]) => {
        if (data?.length <= 0) return []
        const formattedData = JSON.parse(JSON.stringify(data))
        const formatValue = (options: Intl.NumberFormatOptions, value: number) => new Intl.NumberFormat('en-US', options).format(value)

        const dataLength = data.length
        for (let i = 0; i < dataLength; i++) {
            const element = formattedData[i].expenses

            for (let idx = 0; idx < element.length; idx++) {
                const item = element[idx]
                element[idx] = {
                    value: formatValue(
                        {
                            style: item.format,
                            ...(item.format === 'currency' && { currency: 'USD' }),
                            ...(item.decimalDigits && {
                                minimumFractionDigits: item.decimalDigits,
                                maximumFractionDigits: item.decimalDigits
                            })
                        },
                        item.value
                    )
                }
            }
        }
        return formattedData
    }

    formatExpensesData = (data: ITosExpense[]) => {
        if (data?.length <= 0) return []
        const formattedData = JSON.parse(JSON.stringify(data))
        const formatValue = (options: Intl.NumberFormatOptions, value: number) => new Intl.NumberFormat('en-US', options).format(value)

        const dataLength = data.length
        for (let i = 0; i < dataLength; i++) {
            const element = formattedData[i]
            formattedData[i] = {
                value: formatValue(
                    {
                        style: element.format,
                        ...(element.format === 'currency' && { currency: 'USD' }),
                        ...(element.decimalDigits && {
                            minimumFractionDigits: element.decimalDigits,
                            maximumFractionDigits: element.decimalDigits
                        })
                    },
                    element.value
                )
            }
        }
        return formattedData
    }

    formatCostUtilizationData = (data: ICostUtilizationWithFormat[][]) => {
        if (data?.length <= 0) return []
        const formattedData = JSON.parse(JSON.stringify(data))
        const formatValue = (options: Intl.NumberFormatOptions, value: number) => new Intl.NumberFormat('en-US', options).format(value)

        const dataLength = data.length
        for (let i = 0; i < dataLength; i++) {
            const element = formattedData[i]

            for (let idx = 0; idx < element.length; idx++) {
                const item = element[idx]
                const cleanNumber = item?.value?.replace(/,/g, '')
                const parsedNumber = Number(cleanNumber)

                const value = !isNaN(parsedNumber) && item.value !== '' && item.value !== null
                    ? formatValue(
                        {
                            style: item.format,
                            ...(item.format === 'currency' && { currency: 'USD' }),
                            ...(item.decimalDigits && {
                                minimumFractionDigits: item.decimalDigits,
                                maximumFractionDigits: item.decimalDigits
                            }),
                            useGrouping: true
                        },
                        parsedNumber
                    )
                    : item.value

                element[idx] = value
            }
        }
        return formattedData
    }
}

const ContractsAnalyticsManager = new ContractsAnalyticsManagerClass()
export default ContractsAnalyticsManager
